// searchUtils.js
import lunr from 'lunr';
import { US_STATES, getStateAbbreviation } from './usStateUtils';

/**
 * Popular procedures dictionary - ranked by popularity within each category
 * Used when showing procedures for location-based searches
 */
const POPULAR_PROCEDURES = {
  'Breast': ['Breast Augmentation', 'Breast Lift', 'Breast Reduction'],
  'Face': ['Facelift', 'Rhinoplasty', 'Blepharoplasty'],
  'Body': ['Liposuction', 'Tummy Tuck', 'Brazilian Butt Lift'],
  'Injectibles': ['Botox', 'Dermal Fillers', 'Lip Fillers'],
  'Skin': ['Laser Treatment', 'Chemical Peel', 'Microneedling'],
  'Other': []
};

/**
 * Deduplicate procedures by name (keep first occurrence)
 * @param {Array} procedures - Array of procedure objects
 * @returns {Array} Deduplicated procedures
 */
const deduplicateProcedures = (procedures) => {
  const seen = new Set();
  return procedures.filter(proc => {
    // Create a unique key based on procedure name and category
    const key = `${proc.procedureName}_${proc.category}`.toLowerCase();
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

/**
 * Get popular procedures from a clinic's procedure list
 * @param {Array} procedures - Array of procedure objects
 * @returns {Array} Up to 5 popular procedures
 */
export const getPopularProcedures = (procedures) => {
  // First deduplicate all procedures
  const uniqueProcedures = deduplicateProcedures(procedures);
  
  const selected = [];
  const perCategory = {};

  // Select up to 2 per category, prioritize by ranking
  for (const [category, rankedNames] of Object.entries(POPULAR_PROCEDURES)) {
    perCategory[category] = 0;
    
    for (const name of rankedNames) {
      if (selected.length >= 5) break;
      if (perCategory[category] >= 2) break;
      
      const proc = uniqueProcedures.find(p => 
        p.category === category && p.procedureName === name
      );
      
      if (proc) {
        selected.push(proc);
        perCategory[category]++;
      }
    }
  }

  // Fill remaining slots with any procedures not yet selected
  if (selected.length < 5) {
    const selectedNames = new Set(selected.map(p => `${p.procedureName}_${p.category}`.toLowerCase()));
    for (const proc of uniqueProcedures) {
      if (selected.length >= 5) break;
      const key = `${proc.procedureName}_${proc.category}`.toLowerCase();
      if (!selectedNames.has(key)) {
        selected.push(proc);
      }
    }
  }

  return selected;
};

/**
 * Get procedures to display on a clinic card based on search context
 * @param {Object} clinic - Clinic object with procedures array
 * @param {String} searchQuery - Current search query
 * @returns {Array} Up to 5 procedures to display
 */
export const getDisplayProcedures = (clinic, searchQuery) => {
  if (!clinic.procedures || clinic.procedures.length === 0) {
    return [];
  }

  // First deduplicate all procedures from the clinic
  const uniqueProcedures = deduplicateProcedures(clinic.procedures);

  const query = (searchQuery || '').toLowerCase().trim();
  
  // If no search query, return popular procedures
  if (!query) {
    return getPopularProcedures(uniqueProcedures);
  }

  const matchedProcedures = [];
  const categoryMatches = new Set();

  // Find directly matching procedures using fuzzy matching
  uniqueProcedures.forEach(proc => {
    if (procedureNameMatches(proc.procedureName, query)) {
      matchedProcedures.push(proc);
      categoryMatches.add(proc.category);
    }
  });

  // If we found matches, add other procedures from same categories
  if (matchedProcedures.length > 0) {
    const matchedKeys = new Set(matchedProcedures.map(m => `${m.procedureName}_${m.category}`.toLowerCase()));
    const relatedProcedures = uniqueProcedures.filter(proc => {
      const key = `${proc.procedureName}_${proc.category}`.toLowerCase();
      return categoryMatches.has(proc.category) && !matchedKeys.has(key);
    });

    // Combine matched + related, limit to 5 total
    return [...matchedProcedures, ...relatedProcedures].slice(0, 5);
  }

  // If no direct matches (location search), return popular procedures
  return getPopularProcedures(uniqueProcedures);
};

/**
 * Creates an enhanced Lunr search index for clinic-based search
 * @param {Array} clinics - Array of clinic objects to index
 * @param {Object} options - Indexing options with field configurations
 * @returns {Object} Lunr search index and transformed data
 */
export const createSearchIndex = (clinics, options = {}) => {
  // Transform clinics for indexing
  const indexData = clinics.map((clinic, index) => {
    // Concatenate all procedure names for searchability
    const procedureNames = clinic.procedures.map(p => p.procedureName).join(' ');
    const procedureCategories = [...new Set(clinic.procedures.map(p => p.category))].join(' ');
    
    return {
      id: index.toString(),
      clinicName: clinic.clinicName || '',
      city: clinic.city || '',
      state: clinic.state || '',
      address: clinic.address || '',
      clinicCategory: clinic.clinicCategory || '',
      zipCode: clinic.zipCode || '',
      procedureNames: procedureNames,
      procedureCategories: procedureCategories,
      // Store original index for retrieval
      _originalIndex: index
    };
  });

  // Default field configurations for clinic-based search
  const fields = options.fields || {
    zipCode: { boost: 9 },
    state: { boost: 9 },
    city: { boost: 9 },
    clinicName: { boost: 10 },
    procedureNames: { boost: 7 },
    procedureCategories: { boost: 7 },
    clinicCategory: { boost: 5 },
    address: { boost: 3 }
  };

  // Create the Lunr index with enhanced configuration
  const idx = lunr(function() {
    this.ref('id');
    
    // Configure the pipeline for better fuzzy matching
    this.pipeline.remove(lunr.stemmer);
    this.searchPipeline.remove(lunr.stemmer);
    
    // Add the fields to the index
    Object.entries(fields).forEach(([fieldName, config]) => {
      this.field(fieldName, config);
    });
    
    // Add documents to the index
    indexData.forEach(doc => this.add(doc));
  });

  return { idx, indexData };
};

/**
 * State adjacency mapping - defines which states are geographically adjacent
 * Used for finding nearby results when searching by state
 */
const STATE_ADJACENCY = {
  'AL': ['FL', 'GA', 'MS', 'TN'],
  'AK': [], // Alaska has no adjacent states
  'AZ': ['CA', 'CO', 'NM', 'NV', 'UT'],
  'AR': ['LA', 'MO', 'MS', 'OK', 'TN', 'TX'],
  'CA': ['AZ', 'NV', 'OR'],
  'CO': ['AZ', 'KS', 'NE', 'NM', 'OK', 'UT', 'WY'],
  'CT': ['MA', 'NY', 'RI'],
  'DE': ['MD', 'NJ', 'PA'],
  'FL': ['AL', 'GA'],
  'GA': ['AL', 'FL', 'NC', 'SC', 'TN'],
  'HI': [], // Hawaii has no adjacent states
  'ID': ['MT', 'NV', 'OR', 'UT', 'WA', 'WY'],
  'IL': ['IN', 'IA', 'KY', 'MO', 'WI'],
  'IN': ['IL', 'KY', 'MI', 'OH'],
  'IA': ['IL', 'MN', 'MO', 'NE', 'SD', 'WI'],
  'KS': ['CO', 'MO', 'NE', 'OK'],
  'KY': ['IL', 'IN', 'MO', 'OH', 'TN', 'VA', 'WV'],
  'LA': ['AR', 'MS', 'TX'],
  'ME': ['NH'],
  'MD': ['DE', 'PA', 'VA', 'WV'],
  'MA': ['CT', 'NH', 'NY', 'RI', 'VT'],
  'MI': ['IN', 'OH', 'WI'],
  'MN': ['IA', 'ND', 'SD', 'WI'],
  'MS': ['AL', 'AR', 'LA', 'TN'],
  'MO': ['AR', 'IL', 'IA', 'KS', 'KY', 'NE', 'OK', 'TN'],
  'MT': ['ID', 'ND', 'SD', 'WY'],
  'NE': ['CO', 'IA', 'KS', 'MO', 'SD', 'WY'],
  'NV': ['AZ', 'CA', 'ID', 'OR', 'UT'],
  'NH': ['ME', 'MA', 'VT'],
  'NJ': ['DE', 'NY', 'PA'],
  'NM': ['AZ', 'CO', 'OK', 'TX'],
  'NY': ['CT', 'MA', 'NJ', 'PA', 'VT'],
  'NC': ['GA', 'SC', 'TN', 'VA'],
  'ND': ['MN', 'MT', 'SD'],
  'OH': ['IN', 'KY', 'MI', 'PA', 'WV'],
  'OK': ['AR', 'CO', 'KS', 'MO', 'NM', 'TX'],
  'OR': ['CA', 'ID', 'NV', 'WA'],
  'PA': ['DE', 'MD', 'NJ', 'NY', 'OH', 'WV'],
  'RI': ['CT', 'MA'],
  'SC': ['GA', 'NC'],
  'SD': ['IA', 'MN', 'MT', 'ND', 'NE', 'WY'],
  'TN': ['AL', 'AR', 'GA', 'KY', 'MO', 'MS', 'NC', 'VA'],
  'TX': ['AR', 'LA', 'NM', 'OK'],
  'UT': ['AZ', 'CO', 'ID', 'NV', 'WY'],
  'VT': ['MA', 'NH', 'NY'],
  'VA': ['KY', 'MD', 'NC', 'TN', 'WV'],
  'WA': ['ID', 'OR'],
  'WV': ['KY', 'MD', 'OH', 'PA', 'VA'],
  'WI': ['IL', 'IA', 'MI', 'MN'],
  'WY': ['CO', 'ID', 'MT', 'NE', 'SD', 'UT'],
  'DC': ['MD', 'VA']
};

/**
 * Common procedure abbreviations for detection
 */
const PROCEDURE_ABBREVIATIONS = {
  'bbl': 'brazilian butt lift',
  'tummy tuck': 'abdominoplasty',
  'nose job': 'rhinoplasty',
  'boob job': 'breast augmentation',
  'botox': 'botulinum toxin',
  'filler': 'dermal filler',
  'lip filler': 'lip augmentation',
  'face lift': 'facelift',
  'eye lift': 'blepharoplasty',
  'lipo': 'liposuction',
  'mommy makeover': 'mommy makeover',
  'breast aug': 'breast augmentation',
  'breast lift': 'mastopexy'
};

/**
 * Check if a query string is a zip code (5 digits)
 * @param {String} query - Search query
 * @returns {Boolean} True if query is a zip code
 */
export const isZipCode = (query) => {
  const trimmed = query.trim();
  // Check if it's exactly 5 digits
  return /^\d{5}$/.test(trimmed);
};

/**
 * Parse search query to detect location and procedure terms
 * @param {String} query - Search query string
 * @param {Array} clinics - Array of all clinics (to check for valid cities/states)
 * @returns {Object} Parsed query with location and procedure terms
 */
export const parseSearchQuery = (query, clinics = []) => {
  if (!query || !query.trim()) {
    return { locationTerms: [], procedureTerms: [], remainingTerms: [] };
  }

  const trimmed = query.trim().toLowerCase();
  const terms = trimmed.split(/\s+/).filter(t => t.length > 0);
  
  const locationTerms = [];
  const procedureTerms = [];
  const remainingTerms = [];
  
  // Extract unique cities and states from clinics for matching
  const validCities = new Set();
  const validStates = new Set();
  clinics.forEach(clinic => {
    if (clinic.city) validCities.add(clinic.city.toLowerCase());
    if (clinic.state) validStates.add(clinic.state.toLowerCase());
  });

  // Check each term
  for (let i = 0; i < terms.length; i++) {
    const term = terms[i];
    let matched = false;

    // Check for zip code
    if (isZipCode(term)) {
      locationTerms.push({ type: 'zip', value: term });
      matched = true;
    }
    // Check for state abbreviation (2 letters, uppercase)
    else if (term.length === 2 && US_STATES[term.toUpperCase()]) {
      locationTerms.push({ type: 'state', value: term.toUpperCase() });
      matched = true;
    }
    // Check for state full name
    else if (US_STATES[term]) {
      locationTerms.push({ type: 'state', value: US_STATES[term] });
      matched = true;
    }
    // Check for procedure abbreviations FIRST (before city matching to avoid false positives)
    else if (PROCEDURE_ABBREVIATIONS[term]) {
      procedureTerms.push({ 
        type: 'abbreviation', 
        value: term, 
        fullName: PROCEDURE_ABBREVIATIONS[term] 
      });
      matched = true;
    }
    // Check for valid city (must match a city in our clinic data)
    else if (validCities.has(term)) {
      locationTerms.push({ type: 'city', value: term });
      matched = true;
    }
    // Check for multi-word city names (e.g., "Palo Alto", "New York")
    // This handles both cities in our data and potential city names
    else if (i < terms.length - 1) {
      const twoWordCity = `${term} ${terms[i + 1]}`;
      if (validCities.has(twoWordCity)) {
        // Exact match in our clinic data
        locationTerms.push({ type: 'city', value: twoWordCity });
        i++; // Skip next term since we used it
        matched = true;
      } else {
        // Check if it looks like a city name (capitalized words, common patterns)
        // Common city name patterns: "Palo Alto", "New York", "San Francisco", etc.
        const cityPatterns = ['palo', 'san', 'santa', 'los', 'las', 'new', 'mount', 'saint', 'st.', 'fort', 'lake'];
        const firstWord = term.toLowerCase();
        const secondWord = terms[i + 1].toLowerCase();
        
        // If first word matches a common city prefix, treat as potential city
        if (cityPatterns.includes(firstWord) || 
            (firstWord.length > 2 && secondWord.length > 2 && 
             !PROCEDURE_ABBREVIATIONS[twoWordCity] && 
             !PROCEDURE_ABBREVIATIONS[term] && 
             !PROCEDURE_ABBREVIATIONS[secondWord])) {
          locationTerms.push({ type: 'city', value: twoWordCity });
          i++; // Skip next term since we used it
          matched = true;
        }
      }
    }
    
    if (!matched) {
      remainingTerms.push(term);
    }
  }

  // If we have remaining terms and no procedure terms yet, check if they might be procedures
  // by checking against common procedure names
  if (remainingTerms.length > 0 && procedureTerms.length === 0) {
    // Check if any remaining term matches a procedure abbreviation
    for (const term of remainingTerms) {
      if (PROCEDURE_ABBREVIATIONS[term]) {
        const idx = remainingTerms.indexOf(term);
        if (idx !== -1) {
          procedureTerms.push({ 
            type: 'abbreviation', 
            value: term, 
            fullName: PROCEDURE_ABBREVIATIONS[term] 
          });
          remainingTerms.splice(idx, 1);
        }
      }
    }
  }

  return {
    locationTerms,
    procedureTerms,
    remainingTerms,
    hasLocation: locationTerms.length > 0,
    hasProcedure: procedureTerms.length > 0 || remainingTerms.length > 0
  };
};

/**
 * Parse a procedure/doctor/clinic query without location detection.
 * Used for split-field search so geography terms are only read from location input.
 * @param {String} query - Procedure/doctor/clinic query string
 * @returns {Object} Parsed query with procedure terms and remaining terms
 */
export const parseProcedureQuery = (query) => {
  if (!query || !query.trim()) {
    return { procedureTerms: [], remainingTerms: [], hasProcedure: false };
  }

  const terms = query.trim().toLowerCase().split(/\s+/).filter(t => t.length > 0);
  const procedureTerms = [];
  const remainingTerms = [];

  terms.forEach(term => {
    if (PROCEDURE_ABBREVIATIONS[term]) {
      procedureTerms.push({
        type: 'abbreviation',
        value: term,
        fullName: PROCEDURE_ABBREVIATIONS[term]
      });
      return;
    }

    remainingTerms.push(term);
  });

  return {
    procedureTerms,
    remainingTerms,
    hasProcedure: procedureTerms.length > 0 || remainingTerms.length > 0
  };
};

/**
 * Get nearby zip codes (zip codes starting with same first 3 digits)
 * @param {String} zipCode - 5-digit zip code
 * @returns {String} First 3 digits of zip code
 */
const getZipCodePrefix = (zipCode) => {
  if (!zipCode || zipCode.length < 3) return null;
  return zipCode.substring(0, 3);
};

/**
 * Check if a clinic's state matches a state location term
 * Handles both state abbreviations (FL) and full names (Florida)
 * @param {String} clinicState - The clinic's state value (could be "FL" or "Florida")
 * @param {String} stateTermValue - The parsed location term value (always abbreviation like "FL")
 * @returns {Boolean} True if the states match
 */
const matchesState = (clinicState, stateTermValue) => {
  if (!clinicState || !stateTermValue) return false;
  
  const clinicStateUpper = clinicState.toUpperCase().trim();
  const stateAbbrev = stateTermValue.toUpperCase();
  
  // Direct match (both are abbreviations)
  if (clinicStateUpper === stateAbbrev) {
    return true;
  }
  
  // Check if clinic state is the full name that corresponds to our abbreviation
  const fullStateName = US_STATES[stateAbbrev];
  if (fullStateName && clinicStateUpper === fullStateName.toUpperCase()) {
    return true;
  }
  
  // Check if clinic state is a full name and convert to abbreviation for comparison
  const clinicStateLower = clinicState.toLowerCase().trim();
  const clinicStateAbbrev = US_STATES[clinicStateLower];
  if (clinicStateAbbrev && clinicStateAbbrev.toUpperCase() === stateAbbrev) {
    return true;
  }
  
  return false;
};

/**
 * Filter clinics by exact location match (city, state, or zip)
 * Returns only clinics that exactly match ALL location terms (AND logic)
 * @param {Array} clinics - Array of clinics to filter
 * @param {Array} locationTerms - Array of location term objects from parseSearchQuery
 * @returns {Array} Filtered clinics matching exact location criteria
 */
export const filterByLocationExact = (clinics, locationTerms) => {
  if (!locationTerms || locationTerms.length === 0) {
    return clinics;
  }

  // If multiple location terms, use AND logic (clinic must match ALL terms)
  // If single location term, use OR logic (clinic matches the term)
  if (locationTerms.length > 1) {
    return clinics.filter(clinic => {
      return locationTerms.every(locationTerm => {
        if (locationTerm.type === 'zip') {
          return clinic.zipCode && clinic.zipCode === locationTerm.value;
        }
        else if (locationTerm.type === 'state') {
          // State matching: handles both abbreviations (FL) and full names (Florida)
          return matchesState(clinic.state, locationTerm.value);
        }
        else if (locationTerm.type === 'city') {
          const cityLower = locationTerm.value.toLowerCase();
          return clinic.city && clinic.city.toLowerCase() === cityLower;
        }
        return false;
      });
    });
  }

  // Single location term - use OR logic (original behavior)
  const matchedClinics = new Set();
  
  locationTerms.forEach(locationTerm => {
    if (locationTerm.type === 'zip') {
      // Exact zip code match only
      clinics.forEach(clinic => {
        if (clinic.zipCode && clinic.zipCode === locationTerm.value) {
          matchedClinics.add(clinic);
        }
      });
    }
    else if (locationTerm.type === 'state') {
      // State matching: handles both abbreviations (FL) and full names (Florida)
      clinics.forEach(clinic => {
        if (matchesState(clinic.state, locationTerm.value)) {
          matchedClinics.add(clinic);
        }
      });
    }
    else if (locationTerm.type === 'city') {
      // Exact city match only
      const cityLower = locationTerm.value.toLowerCase();
      clinics.forEach(clinic => {
        if (clinic.city && clinic.city.toLowerCase() === cityLower) {
          matchedClinics.add(clinic);
        }
      });
    }
  });

  return Array.from(matchedClinics);
};

/**
 * Filter clinics by nearby location (city, state, or zip)
 * Returns clinics from nearby areas when exact matches are insufficient
 * @param {Array} clinics - Array of clinics to filter
 * @param {Array} locationTerms - Array of location term objects from parseSearchQuery
 * @param {Array} exactMatches - Array of clinics that already matched exactly (to exclude)
 * @returns {Array} Filtered clinics from nearby areas
 */
export const filterByLocationNearby = (clinics, locationTerms, exactMatches = []) => {
  if (!locationTerms || locationTerms.length === 0) {
    return [];
  }

  // Create a set of exact match clinic IDs for exclusion
  const exactMatchIds = new Set(exactMatches.map(c => c.clinicId));

  // Group clinics by normalized state abbreviation and city for proximity matching
  const clinicsByStateAbbr = {};
  const clinicsByCity = {};
  clinics.forEach(clinic => {
    if (clinic.state) {
      const stateAbbr = getStateAbbreviation(clinic.state);
      if (stateAbbr) {
        if (!clinicsByStateAbbr[stateAbbr]) {
          clinicsByStateAbbr[stateAbbr] = [];
        }
        clinicsByStateAbbr[stateAbbr].push(clinic);
      }
    }
    if (clinic.city) {
      const city = clinic.city.toLowerCase();
      if (!clinicsByCity[city]) {
        clinicsByCity[city] = [];
      }
      clinicsByCity[city].push(clinic);
    }
  });

  const nearbyClinics = new Set();
  
  locationTerms.forEach(locationTerm => {
    if (locationTerm.type === 'zip') {
      // Nearby zip codes: same first 3 digits (excluding exact matches)
      const zipPrefix = getZipCodePrefix(locationTerm.value);
      if (zipPrefix) {
        clinics.forEach(clinic => {
          if (clinic.zipCode && 
              clinic.zipCode.length >= 3 && 
              !exactMatchIds.has(clinic.clinicId)) {
            const clinicZipPrefix = getZipCodePrefix(clinic.zipCode);
            if (clinicZipPrefix === zipPrefix) {
              nearbyClinics.add(clinic);
            }
          }
        });
      }
    }
    else if (locationTerm.type === 'state') {
      // Nearby states: adjacent states (excluding exact matches)
      const stateAbbr = locationTerm.value.toUpperCase();
      const adjacentStates = STATE_ADJACENCY[stateAbbr] || [];
      
      adjacentStates.forEach(adjState => {
        if (clinicsByStateAbbr[adjState]) {
          clinicsByStateAbbr[adjState].forEach(clinic => {
            if (!exactMatchIds.has(clinic.clinicId)) {
              nearbyClinics.add(clinic);
            }
          });
        }
      });
    }
    else if (locationTerm.type === 'city') {
      // Nearby cities: other cities in same state first, then adjacent states
      const cityLower = locationTerm.value.toLowerCase();
      
      // Find the state(s) of the searched city from exact matches (normalized to abbreviations)
      const searchedStates = new Set();
      exactMatches.forEach(clinic => {
        if (clinic.state) {
          const stateAbbr = getStateAbbreviation(clinic.state);
          if (stateAbbr) {
            searchedStates.add(stateAbbr);
          }
        }
      });
      
      // Also check if we have a state term in the location terms (e.g., "Miami Florida")
      // This helps us know which state to use for nearby searches
      const stateTerm = locationTerms.find(lt => lt.type === 'state');
      if (stateTerm) {
        searchedStates.add(stateTerm.value.toUpperCase());
      }
      
      // If we have exact matches or a state term, get nearby cities in same state(s)
      if (searchedStates.size > 0) {
        searchedStates.forEach(stateAbbr => {
          if (clinicsByStateAbbr[stateAbbr]) {
            clinicsByStateAbbr[stateAbbr].forEach(clinic => {
              // Exclude exact matches and the searched city itself
              if (!exactMatchIds.has(clinic.clinicId) && 
                  clinic.city && 
                  clinic.city.toLowerCase() !== cityLower) {
                nearbyClinics.add(clinic);
              }
            });
          }
        });
        
        // Also include clinics from adjacent states (cross-border cities)
        searchedStates.forEach(stateAbbr => {
          const adjacentStates = STATE_ADJACENCY[stateAbbr] || [];
          adjacentStates.forEach(adjState => {
            if (clinicsByStateAbbr[adjState]) {
              clinicsByStateAbbr[adjState].forEach(clinic => {
                if (!exactMatchIds.has(clinic.clinicId)) {
                  nearbyClinics.add(clinic);
                }
              });
            }
          });
        });
      }
    }
  });

  return Array.from(nearbyClinics);
};

/**
 * Normalize a string for fuzzy matching by removing spaces and converting to lowercase
 * This handles cases like "micro blading" matching "microblading"
 * @param {String} str - String to normalize
 * @returns {String} Normalized string
 */
const normalizeForFuzzyMatch = (str) => {
  if (!str) return '';
  return str.toLowerCase().replace(/\s+/g, '').trim();
};

/**
 * Check if a procedure name matches a search term using fuzzy matching
 * Handles word splits (e.g., "micro blading" matches "microblading")
 * @param {String} procedureName - Procedure name to check
 * @param {String} searchTerm - Search term to match against
 * @returns {Boolean} True if procedure name matches search term
 */
const procedureNameMatches = (procedureName, searchTerm) => {
  if (!procedureName || !searchTerm) return false;
  
  const procLower = procedureName.toLowerCase();
  const termLower = searchTerm.toLowerCase();
  
  // Strategy 1: Exact match (case-insensitive)
  if (procLower === termLower) return true;
  
  // Strategy 2: Contains match (handles partial matches)
  if (procLower.includes(termLower)) return true;
  
  // Strategy 3: Normalized match (handles word splits)
  // e.g., "micro blading" matches "microblading" and vice versa
  const procNormalized = normalizeForFuzzyMatch(procedureName);
  const termNormalized = normalizeForFuzzyMatch(searchTerm);
  
  if (procNormalized === termNormalized) return true;
  if (procNormalized.includes(termNormalized)) return true;
  if (termNormalized.includes(procNormalized)) return true;
  
  // Strategy 4: Word-based matching (all words in search term appear in procedure name)
  const termWords = termLower.split(/\s+/).filter(w => w.length > 0);
  if (termWords.length > 1) {
    const allWordsMatch = termWords.every(word => {
      // Check if word appears in procedure name (with or without spaces)
      return procLower.includes(word) || procNormalized.includes(normalizeForFuzzyMatch(word));
    });
    if (allWordsMatch) return true;
  }
  
  // Strategy 5: Character sequence matching (handles minor variations)
  // Check if the normalized search term appears as a sequence in the normalized procedure name
  if (procNormalized.length >= termNormalized.length) {
    // Try sliding window approach for character sequence matching
    for (let i = 0; i <= procNormalized.length - termNormalized.length; i++) {
      const window = procNormalized.substring(i, i + termNormalized.length);
      if (window === termNormalized) return true;
    }
  }
  
  return false;
};

/**
 * Filter clinics by procedure terms with improved fuzzy matching
 * @param {Array} clinics - Array of clinics to filter
 * @param {Array} procedureTerms - Array of procedure term objects from parseSearchQuery
 * @param {Array} remainingTerms - Remaining search terms that might be procedure names
 * @returns {Array} Filtered clinics that offer matching procedures
 */
export const filterByProcedure = (clinics, procedureTerms, remainingTerms) => {
  if ((!procedureTerms || procedureTerms.length === 0) && (!remainingTerms || remainingTerms.length === 0)) {
    return clinics;
  }

  const searchTerms = [];
  
  // Add procedure abbreviation full names
  procedureTerms.forEach(term => {
    if (term.fullName) {
      searchTerms.push(term.fullName.toLowerCase());
    }
    searchTerms.push(term.value.toLowerCase());
  });
  
  // Add remaining terms as potential procedure names
  if (remainingTerms && remainingTerms.length > 0) {
    searchTerms.push(...remainingTerms.map(t => t.toLowerCase()));
  }

  const filtered = clinics.filter(clinic => {
    if (!clinic.procedures || clinic.procedures.length === 0) {
      return false;
    }

    // Check if any procedure matches any search term using fuzzy matching
    return clinic.procedures.some(proc => {
      return searchTerms.some(term => procedureNameMatches(proc.procedureName, term));
    });
  });

  return filtered;
};

/**
 * Check if a query matches clinic names (prioritize clinic name searches)
 * @param {Array} clinics - Array of clinics to search
 * @param {String} query - Search query
 * @param {Object} searchIndex - Optional Lunr search index for fuzzy matching
 * @returns {Array|null} Array of matching clinics, or null if no matches found
 */
const searchByClinicName = (clinics, query, searchIndex = null) => {
  if (!query || !query.trim() || !clinics || clinics.length === 0) {
    return null;
  }

  const trimmedQuery = query.trim().toLowerCase();
  const queryWords = trimmedQuery.split(/\s+/).filter(w => w.length > 0);
  
  // Strategy 1: Exact match (case-insensitive)
  let exactMatches = clinics.filter(clinic => {
    if (!clinic.clinicName) return false;
    return clinic.clinicName.toLowerCase() === trimmedQuery;
  });
  
  if (exactMatches.length > 0) {
    return exactMatches;
  }
  
  // Strategy 2: Partial match - query is contained in clinic name
  let partialMatches = clinics.filter(clinic => {
    if (!clinic.clinicName) return false;
    const clinicNameLower = clinic.clinicName.toLowerCase();
    return clinicNameLower.includes(trimmedQuery);
  });
  
  if (partialMatches.length > 0) {
    return partialMatches;
  }
  
  // Strategy 3: Word-based matching - all query words appear in clinic name
  if (queryWords.length > 1) {
    let wordMatches = clinics.filter(clinic => {
      if (!clinic.clinicName) return false;
      const clinicNameLower = clinic.clinicName.toLowerCase();
      // Check if all query words appear in the clinic name
      return queryWords.every(word => clinicNameLower.includes(word));
    });
    
    if (wordMatches.length > 0) {
      return wordMatches;
    }
  }
  
  // Strategy 4: Use Lunr search index for fuzzy matching if available
  if (searchIndex) {
    try {
      // Try exact search first
      let lunrResults = searchIndex.search(trimmedQuery);
      
      // If no results, try fuzzy matching
      if (lunrResults.length === 0) {
        const fuzzyQuery = queryWords.map(term => `${term}~1`).join(' ');
        lunrResults = searchIndex.search(fuzzyQuery);
      }
      
      // If still no results, try wildcard matching
      if (lunrResults.length === 0) {
        const wildcardQuery = queryWords.map(term => `${term}*`).join(' ');
        lunrResults = searchIndex.search(wildcardQuery);
      }
      
      // If we have results, filter to only clinic name matches (not location/procedure matches)
      if (lunrResults.length > 0) {
        const clinicNameMatches = lunrResults
          .map(result => clinics[parseInt(result.ref)])
          .filter(clinic => {
            if (!clinic || !clinic.clinicName) return false;
            const clinicNameLower = clinic.clinicName.toLowerCase();
            // Check if query matches clinic name (not just location/procedure)
            return clinicNameLower.includes(trimmedQuery) || 
                   queryWords.some(word => clinicNameLower.includes(word));
          });
        
        if (clinicNameMatches.length > 0) {
          return clinicNameMatches;
        }
      }
    } catch (error) {
      // If Lunr search fails, continue to next strategy
      console.error('Lunr search error in clinic name matching:', error);
    }
  }
  
  // Strategy 5: Individual word matching (for queries like "Gillian" matching "Gillian Institute")
  if (queryWords.length === 1 && queryWords[0].length >= 3) {
    const singleWord = queryWords[0];
    let singleWordMatches = clinics.filter(clinic => {
      if (!clinic.clinicName) return false;
      const clinicNameLower = clinic.clinicName.toLowerCase();
      // Check if the word appears as a whole word in the clinic name
      const words = clinicNameLower.split(/\s+/);
      return words.some(word => word === singleWord || word.startsWith(singleWord));
    });
    
    if (singleWordMatches.length > 0) {
      return singleWordMatches;
    }
  }
  
  return null;
};

/**
 * Category mapping: maps search terms to actual category values
 * Handles fuzzy matching (spaces, case-insensitive, variations)
 */
const CATEGORY_MAPPINGS = {
  // Plastic Surgery variations
  'plastic surgery': 'Plastic Surgery',
  'plasticsurgery': 'Plastic Surgery',
  'plastic': 'Plastic Surgery',
  // Note: 'surgery' alone is too ambiguous, only match when combined with "plastic"
  
  // Medspa / Aesthetics variations
  'medspa / aesthetics': 'Medspa / Aesthetics',
  'med spa / aesthetics': 'Medspa / Aesthetics',
  'medspa/aesthetics': 'Medspa / Aesthetics',
  'medspa': 'Medspa / Aesthetics',
  'med spa': 'Medspa / Aesthetics',
  'medspas': 'Medspa / Aesthetics',
  'aesthetics': 'Medspa / Aesthetics',
  'aesthetic': 'Medspa / Aesthetics',
  'medical spa': 'Medspa / Aesthetics',
  'medicalspa': 'Medspa / Aesthetics',
  
  // Medical
  'medical': 'Medical',
  
  // Dermatology
  'dermatology': 'Dermatology',
  'dermatologist': 'Dermatology',
  'derm': 'Dermatology',
  
  // Other
  'other': 'Other'
};

/**
 * Normalize a query for category matching (remove spaces, lowercase, etc.)
 * @param {String} query - Search query
 * @returns {String} Normalized query
 */
const normalizeCategoryQuery = (query) => {
  if (!query) return '';
  return query.trim().toLowerCase().replace(/\s+/g, ' ');
};

/**
 * Detect if a query matches a category
 * @param {String} query - Search query
 * @returns {String|null} Category name if matched, null otherwise
 */
export const detectCategory = (query) => {
  if (!query || !query.trim()) return null;
  
  const normalized = normalizeCategoryQuery(query);
  
  // Direct match
  if (CATEGORY_MAPPINGS[normalized]) {
    return CATEGORY_MAPPINGS[normalized];
  }
  
  // Check for partial matches (e.g., "medspa" in "medspa clinic")
  for (const [key, category] of Object.entries(CATEGORY_MAPPINGS)) {
    // Check if the normalized query contains the category key
    if (normalized.includes(key) && key.length >= 3) {
      return category;
    }
    // Check if the category key contains the normalized query (for partial matches)
    if (key.includes(normalized) && normalized.length >= 3) {
      return category;
    }
  }
  
  // Special case: "plastic surgery" - check if both words appear (order doesn't matter)
  if (normalized.includes('plastic') && normalized.includes('surgery')) {
    return 'Plastic Surgery';
  }
  
  // Special case: "med spa" or "medical spa" variations
  // Match if query contains both "med/medical" AND ("spa" OR "aesthetic")
  if ((normalized.includes('med') || normalized.includes('medical')) && 
      (normalized.includes('spa') || normalized.includes('aesthetic'))) {
    return 'Medspa / Aesthetics';
  }
  
  // Special case: "aesthetic" or "aesthetics" alone should match Medspa / Aesthetics
  // But only if it's not part of a longer word (e.g., "aesthetician" should not match)
  if (normalized === 'aesthetic' || normalized === 'aesthetics') {
    return 'Medspa / Aesthetics';
  }
  
  return null;
};

/**
 * Check if a clinic name contains the search term (fuzzy matching)
 * @param {String} clinicName - Clinic name to check
 * @param {String} searchTerm - Search term to look for
 * @returns {Boolean} True if clinic name contains search term
 */
const clinicNameContainsTerm = (clinicName, searchTerm) => {
  if (!clinicName || !searchTerm) return false;
  
  const clinicNameLower = clinicName.toLowerCase();
  const normalizedClinicName = clinicNameLower.replace(/\s+/g, ' ');
  const searchTermLower = normalizeCategoryQuery(searchTerm);
  const searchWords = searchTermLower.split(/\s+/).filter(w => w.length > 0);
  
  // Check if any search word appears in clinic name (fuzzy matching)
  return searchWords.some(word => {
    // Exact word match
    if (normalizedClinicName.includes(word)) return true;
    // Word without spaces match (e.g., "medspa" matches "med spa")
    const wordNoSpaces = word.replace(/\s+/g, '');
    const clinicNameNoSpaces = normalizedClinicName.replace(/\s+/g, '');
    if (clinicNameNoSpaces.includes(wordNoSpaces)) return true;
    return false;
  });
};

/**
 * Search clinics by category, prioritizing clinics with search term in name
 * Returns ALL clinics in category PLUS clinics with search term in name (even if not in category)
 * @param {Array} clinics - Array of clinics to search
 * @param {String} category - Category to filter by
 * @param {String} searchTerm - Original search term (for name prioritization)
 * @returns {Array} Array of clinics: name matches first (in or out of category), then other category clinics
 */
const searchByCategory = (clinics, category, searchTerm = '') => {
  if (!clinics || clinics.length === 0 || !category) {
    return [];
  }
  
  // Filter clinics by category (handle both "Medspa / Aesthetics" and "Med Spa / Aesthetics")
  const categoryLower = category.toLowerCase();
  const normalizedCategory = categoryLower.includes('medspa') || categoryLower.includes('med spa') 
    ? ['Medspa / Aesthetics', 'Med Spa / Aesthetics']
    : [category];
  
  const categoryClinics = clinics.filter(clinic => {
    if (!clinic.clinicCategory) return false;
    const clinicCategoryLower = clinic.clinicCategory.toLowerCase();
    return normalizedCategory.some(cat => 
      clinicCategoryLower === cat.toLowerCase()
    );
  });
  
  // If no search term provided, return all category clinics
  if (!searchTerm || !searchTerm.trim()) {
    return categoryClinics;
  }
  
  // Get clinics with search term in name (even if not in category)
  const nameMatchClinics = clinics.filter(clinic => {
    if (!clinic.clinicName) return false;
    return clinicNameContainsTerm(clinic.clinicName, searchTerm);
  });
  
  // Create a set of clinic IDs to track what we've included
  const includedClinicIds = new Set();
  
  // Group 1: Name matches that are ALSO in category (highest priority)
  const nameMatchesInCategory = [];
  nameMatchClinics.forEach(clinic => {
    const isInCategory = categoryClinics.some(catClinic => catClinic.clinicId === clinic.clinicId);
    if (isInCategory) {
      nameMatchesInCategory.push({ ...clinic, _isNameMatch: true, _isInCategory: true });
      includedClinicIds.add(clinic.clinicId);
    }
  });
  
  // Group 2: Name matches that are NOT in category (second priority)
  // Mark these with a flag so they can be preserved even when category filter is applied
  const nameMatchesNotInCategory = [];
  nameMatchClinics.forEach(clinic => {
    if (!includedClinicIds.has(clinic.clinicId)) {
      nameMatchesNotInCategory.push({ ...clinic, _isNameMatch: true, _isInCategory: false });
      includedClinicIds.add(clinic.clinicId);
    }
  });
  
  // Group 3: Category clinics without name match (lowest priority)
  const categoryClinicsWithoutNameMatch = categoryClinics.filter(clinic => {
    return !includedClinicIds.has(clinic.clinicId);
  }).map(clinic => ({ ...clinic, _isNameMatch: false, _isInCategory: true }));
  
  // Return prioritized results: name matches in category, then name matches outside category, then other category clinics
  return [
    ...nameMatchesInCategory,
    ...nameMatchesNotInCategory,
    ...categoryClinicsWithoutNameMatch
  ];
};

/**
 * Performs a search with location-aware filtering and AND logic for combined queries
 * Returns exact matches first, then nearby results if exact matches < 9
 * @param {Object} searchIndex - Lunr search index
 * @param {Array} clinics - Original clinics array
 * @param {String} query - Search query
 * @param {Number} minResults - Minimum number of results to fill (default: 9)
 * @returns {Object} Object with exactResults, nearbyResults, and metadata
 */
export const performSearch = (searchIndex, clinics, query, minResults = 9) => {
  if (!query || !query.trim()) {
    return { 
      exactResults: clinics, 
      nearbyResults: [], 
      isLocationSearch: false,
      hasNearbyResults: false
    };
  }

  const trimmedQuery = query.trim();

  // PRIORITY 1: Check for category matches FIRST (e.g., "medspa", "med spa", "aesthetics")
  // Category searches should return ALL clinics in category PLUS clinics with term in name
  // This takes priority over pure clinic name searches to ensure category coverage
  const detectedCategory = detectCategory(trimmedQuery);
  if (detectedCategory) {
    const categoryResults = searchByCategory(clinics, detectedCategory, trimmedQuery);
    if (categoryResults.length > 0) {
      return {
        exactResults: categoryResults,
        nearbyResults: [],
        isLocationSearch: false,
        hasNearbyResults: false,
        isCategorySearch: true,
        category: detectedCategory
      };
    }
  }

  // PRIORITY 2: Check for clinic name matches (only if no category was detected)
  // This ensures searches like "Gillian Institute", "Gillian", or "Institute" work correctly
  const clinicNameMatches = searchByClinicName(clinics, trimmedQuery, searchIndex);
  if (clinicNameMatches && clinicNameMatches.length > 0) {
    return {
      exactResults: clinicNameMatches,
      nearbyResults: [],
      isLocationSearch: false,
      hasNearbyResults: false,
      isClinicNameSearch: true
    };
  }

  // PRIORITY 3: Parse the query to detect location and procedure terms
  // Only proceed with location/procedure logic if no clinic name or category matches were found
  const parsed = parseSearchQuery(trimmedQuery, clinics);
  
  // If we detected location terms, use location-first filtering with exact/nearby separation
  if (parsed.hasLocation) {
    // Get exact location matches first (ALL clinics from exact location, regardless of procedure)
    let exactLocationMatches = filterByLocationExact(clinics, parsed.locationTerms);
    
    // Get nearby location matches (for potential use if we need more results)
    let nearbyLocationMatches = filterByLocationNearby(clinics, parsed.locationTerms, exactLocationMatches);
    
    // If we have procedure terms, filter both exact and nearby by procedure
    let exactMatches = exactLocationMatches;
    let nearbyCandidates = nearbyLocationMatches;
    
    if (parsed.hasProcedure) {
      exactMatches = filterByProcedure(exactLocationMatches, parsed.procedureTerms, parsed.remainingTerms);
      nearbyCandidates = filterByProcedure(nearbyLocationMatches, parsed.procedureTerms, parsed.remainingTerms);
    }
    
    // If we have fewer than minResults exact matches, get nearby results to fill up to minResults
    let nearbyResults = [];
    const hasNearbyResults = exactMatches.length < minResults;
    
    if (hasNearbyResults) {
      // Limit nearby results to fill up to minResults total
      const needed = minResults - exactMatches.length;
      nearbyResults = nearbyCandidates.slice(0, needed);
    }
    
    return { 
      exactResults: exactMatches,
      nearbyResults: nearbyResults,
      isLocationSearch: true,
      hasNearbyResults: hasNearbyResults,
      hasProcedure: parsed.hasProcedure
    };
  }
  
  // If we only have procedure terms (no location), use existing search logic
  // But still check for zip code as a special case
  if (isZipCode(trimmedQuery)) {
    const zipPrefix = getZipCodePrefix(trimmedQuery);
    
    // Get exact zip code matches
    const exactZipMatches = clinics.filter(clinic => {
      if (!clinic.zipCode) return false;
      return clinic.zipCode === trimmedQuery;
    });
    
    // If we have fewer than minResults, get nearby zip codes
    let nearbyZipResults = [];
    const hasNearbyZipResults = exactZipMatches.length < minResults;
    
    if (hasNearbyZipResults) {
      const nearbyZipCandidates = clinics.filter(clinic => {
        if (!clinic.zipCode || clinic.zipCode === trimmedQuery) return false;
        
        // Nearby zip codes (same first 3 digits)
        if (zipPrefix && clinic.zipCode.length >= 3) {
          const clinicZipPrefix = getZipCodePrefix(clinic.zipCode);
          return clinicZipPrefix === zipPrefix;
        }
        
        return false;
      });
      
      const needed = minResults - exactZipMatches.length;
      nearbyZipResults = nearbyZipCandidates.slice(0, needed);
    }
    
    return { 
      exactResults: exactZipMatches,
      nearbyResults: nearbyZipResults,
      isLocationSearch: true,
      hasNearbyResults: hasNearbyZipResults
    };
  }

  // If we have procedure terms but no location, filter by procedure first
  if (parsed.hasProcedure && !parsed.hasLocation) {
    const procedureFiltered = filterByProcedure(clinics, parsed.procedureTerms, parsed.remainingTerms);
    return { 
      exactResults: procedureFiltered,
      nearbyResults: [],
      isLocationSearch: false,
      hasNearbyResults: false
    };
  }

  // Fall back to existing Lunr.js search for general queries
  try {
    // Strategy 1: Try exact search first
    let searchResults = searchIndex.search(query);
    
    // Strategy 2: If no results, try normalized search (handles word splits like "micro blading" vs "microblading")
    if (searchResults.length === 0 && query.includes(' ')) {
      // Try searching with spaces removed (normalized version)
      const normalizedQuery = normalizeForFuzzyMatch(query);
      if (normalizedQuery.length >= 3) {
        searchResults = searchIndex.search(normalizedQuery);
      }
      
      // Also try searching for the normalized query with wildcard
      if (searchResults.length === 0 && normalizedQuery.length >= 3) {
        searchResults = searchIndex.search(`${normalizedQuery}*`);
      }
    }
    
    // Strategy 3: If no results, try with fuzzy matching
    if (searchResults.length === 0) {
      // Add fuzzy matching operator to each term
      const fuzzyQuery = query
        .trim()
        .split(/\s+/)
        .map(term => `${term}~1`) // ~1 specifies edit distance of 1 (more fuzzy with ~2)
        .join(' ');
      
      searchResults = searchIndex.search(fuzzyQuery);
    }
    
    // Strategy 4: If still no results, try with wildcard matching
    if (searchResults.length === 0) {
      // Add wildcard operator to each term
      const wildcardQuery = query
        .trim()
        .split(/\s+/)
        .map(term => `${term}*`) // * is wildcard for prefix matching
        .join(' ');
      
      searchResults = searchIndex.search(wildcardQuery);
    }

    // Strategy 5: If still no results, try removing extra characters (like "breasts" -> "breast")
    if (searchResults.length === 0) {
      // Try progressively shortening each term by 1-3 characters from the end
      const terms = query.trim().split(/\s+/);
      
      for (const term of terms) {
        if (term.length < 5) continue; // Only process longer terms that might have suffixes
        
        // Try removing 1, 2, or 3 characters from the end
        for (let i = 1; i <= Math.min(3, term.length - 2); i++) {
          const shortenedTerm = term.substring(0, term.length - i);
          if (shortenedTerm.length < 3) continue; // Keep terms reasonably long
          
          const shortenedResults = searchIndex.search(shortenedTerm);
          if (shortenedResults.length > 0) {
            searchResults = [...searchResults, ...shortenedResults];
          }
        }
      }
      
      // Remove duplicates if we found results
      if (searchResults.length > 0) {
        const uniqueRefs = new Set();
        searchResults = searchResults.filter(result => {
          if (uniqueRefs.has(result.ref)) return false;
          uniqueRefs.add(result.ref);
          return true;
        });
      }
    }
    
    // Strategy 6: If still no results, try individual terms with fuzzy matching
    if (searchResults.length === 0 && query.includes(' ')) {
      const terms = query.trim().split(/\s+/);
      
      // Search for each term individually with fuzzy matching
      for (const term of terms) {
        if (term.length < 2) continue; // Skip very short terms
        
        const termResults = searchIndex.search(`${term}~1`);
        searchResults = [...searchResults, ...termResults];
      }
      
      // Remove duplicates
      const uniqueRefs = new Set();
      searchResults = searchResults.filter(result => {
        if (uniqueRefs.has(result.ref)) return false;
        uniqueRefs.add(result.ref);
        return true;
      });
    }
    
    // If we have results from any strategy, map them back to the original clinics
    if (searchResults.length > 0) {
      const lunrResults = searchResults.map(result => clinics[parseInt(result.ref)]);
      return { 
        exactResults: lunrResults,
        nearbyResults: [],
        isLocationSearch: false,
        hasNearbyResults: false
      };
    }
    
    // Final fallback: fuzzy matching search on clinic data
    const fallbackResults = clinics.filter(clinic => {
      const searchLower = query.toLowerCase();
      const searchNormalized = normalizeForFuzzyMatch(query);
      
      // Check clinic name with fuzzy matching
      if (clinic.clinicName) {
        const clinicNameLower = clinic.clinicName.toLowerCase();
        if (clinicNameLower.includes(searchLower) || 
            normalizeForFuzzyMatch(clinic.clinicName).includes(searchNormalized)) {
          return true;
        }
      }
      
      // Check location fields
      if (clinic.city && clinic.city.toLowerCase().includes(searchLower)) return true;
      if (clinic.state && clinic.state.toLowerCase().includes(searchLower)) return true;
      if (clinic.address && clinic.address.toLowerCase().includes(searchLower)) return true;
      if (clinic.clinicCategory && clinic.clinicCategory.toLowerCase().includes(searchLower)) return true;
      
      // Check procedures with fuzzy matching
      if (clinic.procedures && clinic.procedures.length > 0) {
        const procedureNames = clinic.procedures.map(p => p.procedureName.toLowerCase()).join(' ');
        const procedureNamesNormalized = clinic.procedures.map(p => normalizeForFuzzyMatch(p.procedureName)).join(' ');
        const categories = clinic.procedures.map(p => p.category.toLowerCase()).join(' ');
        
        if (procedureNames.includes(searchLower) || 
            procedureNamesNormalized.includes(searchNormalized) ||
            categories.includes(searchLower)) {
          return true;
        }
        
        // Also check individual procedures with fuzzy matching
        if (clinic.procedures.some(proc => procedureNameMatches(proc.procedureName, query))) {
          return true;
        }
      }
      
      return false;
    });
    
    return { 
      exactResults: fallbackResults,
      nearbyResults: [],
      isLocationSearch: false,
      hasNearbyResults: false
    };
  } catch (error) {
    console.error('Search error:', error);
    
    // Fall back to fuzzy matching if Lunr search fails
    const errorFallbackResults = clinics.filter(clinic => {
      const searchLower = query.toLowerCase();
      const searchNormalized = normalizeForFuzzyMatch(query);
      
      // Check clinic name with fuzzy matching
      if (clinic.clinicName) {
        const clinicNameLower = clinic.clinicName.toLowerCase();
        if (clinicNameLower.includes(searchLower) || 
            normalizeForFuzzyMatch(clinic.clinicName).includes(searchNormalized)) {
          return true;
        }
      }
      
      // Check location fields
      if (clinic.city && clinic.city.toLowerCase().includes(searchLower)) return true;
      if (clinic.state && clinic.state.toLowerCase().includes(searchLower)) return true;
      
      // Check procedures with fuzzy matching
      if (clinic.procedures && clinic.procedures.length > 0) {
        const procedureNames = clinic.procedures.map(p => p.procedureName.toLowerCase()).join(' ');
        const procedureNamesNormalized = clinic.procedures.map(p => normalizeForFuzzyMatch(p.procedureName)).join(' ');
        
        if (procedureNames.includes(searchLower) || 
            procedureNamesNormalized.includes(searchNormalized)) {
          return true;
        }
        
        // Also check individual procedures with fuzzy matching
        if (clinic.procedures.some(proc => procedureNameMatches(proc.procedureName, query))) {
          return true;
        }
      }
      
      return false;
    });
    
    return { 
      exactResults: errorFallbackResults,
      nearbyResults: [],
      isLocationSearch: false,
      hasNearbyResults: false
    };
  }
};

/**
 * Applies filters to a set of clinics based on clinic category and procedure prices
 * @param {Array} clinics - Array of clinics to filter
 * @param {Object} filters - Object containing filter criteria
 * @returns {Array} Filtered clinics with procedures filtered to match criteria
 */
export const applyFilters = (clinics, filters) => {
  if (!clinics || clinics.length === 0) {
    return [];
  }

  return clinics
    .filter(clinic => {
      // Category filter - filter by clinic category, not procedure category
      // BUT: preserve clinics marked as name matches (_isNameMatch: true) even if they don't match category
      // Also preserve clinics with search term in name when both category filter and search query are present
      if (filters.category) {
        // If clinic is a name match (from category search), preserve it even if category doesn't match
        if (clinic._isNameMatch && clinic._isInCategory === false) {
          return true;
        }
        
        // If we have both a category filter and a search query, also preserve clinics with search term in name
        // This handles cases where user manually sets category filter AND searches for a term
        if (filters.searchQuery && filters.searchQuery.trim()) {
          const searchTerm = filters.searchQuery.trim().toLowerCase();
          if (clinic.clinicName && clinicNameContainsTerm(clinic.clinicName, searchTerm)) {
            // Check if this clinic is NOT in the filtered category
            const clinicCategoryLower = (clinic.clinicCategory || '').toLowerCase();
            const filterCategoryLower = filters.category.toLowerCase();
            if (clinicCategoryLower !== filterCategoryLower) {
              // This is a name match but not in the category - preserve it
              return true;
            }
          }
        }
        
        if (!clinic.clinicCategory) {
          return false;
        }
        return clinic.clinicCategory.toLowerCase() === filters.category.toLowerCase();
      }
      return true;
    })
    .map(clinic => {
      // Filter the procedures within the clinic for price ranges only
      let filteredProcedures = [...clinic.procedures];

      // Price range filters - only keep procedures in price range
      if (filters.minPrice) {
        filteredProcedures = filteredProcedures.filter(proc => 
          proc.price >= parseFloat(filters.minPrice)
        );
      }

      if (filters.maxPrice) {
        filteredProcedures = filteredProcedures.filter(proc => 
          proc.price <= parseFloat(filters.maxPrice)
        );
      }

      // Return clinic with filtered procedures
      return {
        ...clinic,
        procedures: filteredProcedures
      };
    })
    // Only keep clinics that have procedures after filtering
    .filter(clinic => clinic.procedures.length > 0);
};

/**
 * Handles pagination of results
 * @param {Array} results - Array of filtered results
 * @param {Number} page - Current page number (1-based)
 * @param {Number} limit - Number of items per page
 * @returns {Object} Pagination information and paginated results
 */
export const paginateResults = (results, page = 1, limit = 10) => {
  const startIndex = (page - 1) * limit;
  const endIndex = startIndex + limit;
  
  return {
    total: results.length,
    totalPages: Math.ceil(results.length / limit),
    currentPage: page,
    results: results.slice(startIndex, endIndex)
  };
};

/**
 * Calculate distance between two coordinates using Haversine formula
 * @param {Number} lat1 - Latitude of first point
 * @param {Number} lon1 - Longitude of first point
 * @param {Number} lat2 - Latitude of second point
 * @param {Number} lon2 - Longitude of second point
 * @returns {Number} Distance in kilometers
 */
export const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Radius of Earth in kilometers
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

/**
 * Calculate relevance score for a clinic based on search context
 * @param {Object} clinic - Clinic object
 * @param {String} searchQuery - Search query string
 * @param {Object} searchResult - Lunr search result (contains score)
 * @param {Object} userLocation - User's location { lat, lng }
 * @returns {Number} Relevance score (higher is more relevant)
 */
export const calculateRelevanceScore = (clinic, searchQuery, searchResult = null, userLocation = null) => {
  let score = 0;
  const query = (searchQuery || '').toLowerCase().trim();
  
  // 1. Search Match Score (0-100 points, weight: 10x)
  let searchMatchScore = 0;
  
  if (query && searchResult) {
    // Base score from Lunr (0-10, normalize to 0-10)
    searchMatchScore = Math.min(searchResult.score || 0, 10);
    
    // Exact match bonuses
    const clinicName = (clinic.clinicName || '').toLowerCase();
    const city = (clinic.city || '').toLowerCase();
    const state = (clinic.state || '').toLowerCase();
    
    if (clinicName === query) searchMatchScore += 5; // Exact clinic name match
    else if (clinicName.includes(query)) searchMatchScore += 2; // Partial clinic name match
    
    if (city === query || state === query) searchMatchScore += 3; // Exact location match
    else if (city.includes(query) || state.includes(query)) searchMatchScore += 1; // Partial location match
  } else if (!query) {
    // No search query - base score for all
    searchMatchScore = 5;
  }
  
  score += searchMatchScore * 10; // 0-100 points
  
  // 2. Procedure Match Score (0-50 points, weight: 5x)
  let procedureMatchScore = 0;
  
  if (query && clinic.procedures && clinic.procedures.length > 0) {
    let exactMatches = 0;
    let partialMatches = 0;
    let categoryMatches = 0;
    
    clinic.procedures.forEach(proc => {
      const procName = (proc.procedureName || '').toLowerCase();
      const procCategory = (proc.category || '').toLowerCase();
      
      if (procName === query) {
        exactMatches++;
      } else if (procName.includes(query)) {
        partialMatches++;
      }
      
      if (procCategory === query || procCategory.includes(query)) {
        categoryMatches++;
      }
    });
    
    procedureMatchScore = Math.min(
      (exactMatches * 3) + (partialMatches * 1) + (categoryMatches * 0.5),
      10
    );
  } else if (!query && clinic.procedures) {
    // No query - score based on procedure variety
    procedureMatchScore = Math.min(clinic.procedures.length * 0.1, 5);
  }
  
  score += procedureMatchScore * 5; // 0-50 points
  
  // 3. Rating Score (0-10 points, weight: 2x)
  const rating = clinic.rating || 0;
  score += rating * 2; // 0-10 points
  
  // 4. Review Count Score (0-5 points, weight: 0.1x)
  const reviewCount = clinic.reviewCount || 0;
  score += Math.min(reviewCount, 50) * 0.1; // 0-5 points
  
  // 5. Distance Penalty (subtract points based on distance)
  if (!query && userLocation && clinic.latitude && clinic.longitude) {
    // When no search query, prioritize nearby locations
    const distance = calculateDistance(
      userLocation.lat,
      userLocation.lng,
      clinic.latitude,
      clinic.longitude
    );
    score -= distance * 0.01; // Penalty increases with distance
  }
  
  return score;
};

/**
 * Sort clinics based on specified sort mode
 * @param {Array} clinics - Array of clinics to sort
 * @param {String} sortBy - Sort mode: 'relevance', 'rating', 'reviewCount'
 * @param {String} searchQuery - Current search query
 * @param {Array} searchResults - Lunr search results (for relevance scoring)
 * @param {Object} userLocation - User's location { lat, lng }
 * @returns {Array} Sorted clinics
 */
export const sortResults = (clinics, sortBy = 'relevance', searchQuery = '', searchResults = [], userLocation = null) => {
  if (!clinics || clinics.length === 0) {
    return [];
  }
  
  // Create a copy to avoid mutating original array
  const clinicsToSort = [...clinics];
  
  switch (sortBy) {
    case 'rating':
      // Sort by average rating (highest first), then by review count as tiebreaker
      return clinicsToSort.sort((a, b) => {
        const ratingDiff = (b.rating || 0) - (a.rating || 0);
        if (ratingDiff !== 0) return ratingDiff;
        return (b.reviewCount || 0) - (a.reviewCount || 0);
      });
      
    case 'reviewCount':
      // Sort by review count (most reviews first), then by rating as tiebreaker
      return clinicsToSort.sort((a, b) => {
        const reviewDiff = (b.reviewCount || 0) - (a.reviewCount || 0);
        if (reviewDiff !== 0) return reviewDiff;
        return (b.rating || 0) - (a.rating || 0);
      });
      
    case 'relevance':
    default:
      // Build a map of clinic IDs to search results for quick lookup
      const searchResultMap = {};
      if (searchResults && searchResults.length > 0) {
        searchResults.forEach(result => {
          const clinicIndex = parseInt(result.ref);
          searchResultMap[clinicIndex] = result;
        });
      }
      
      // Calculate relevance score for each clinic and sort
      const clinicsWithScores = clinicsToSort.map((clinic, index) => {
        const searchResult = searchResultMap[index] || null;
        const relevanceScore = calculateRelevanceScore(
          clinic,
          searchQuery,
          searchResult,
          userLocation
        );
        
        return {
          clinic,
          score: relevanceScore
        };
      });
      
      // Sort by relevance score (highest first)
      clinicsWithScores.sort((a, b) => b.score - a.score);
      
      return clinicsWithScores.map(item => item.clinic);
  }
};